import { Image as ImageIcon, AlertCircle } from "lucide-react"
import { ImageWithFallback } from "@/components/ui/image-with-fallback"
import {
  useImageUrl,
  type EventGalleryImage,
} from "@/hooks/useEventGallery"

interface GalleryImageProps {
  image: EventGalleryImage
  onClick: () => void
}

// Image component that handles signed URLs with better error handling
export const GalleryImage: React.FC<GalleryImageProps> = ({ image, onClick }) => {
  const { data: imageUrl, isLoading, error } = useImageUrl(image.file_path)

  console.log('GalleryImage render:', {
    imageName: image.name,
    filePath: image.file_path,
    imageUrl,
    isLoading,
    error
  })

  if (isLoading) {
    return (
      <div className="w-full h-full bg-gray-200 rounded-md flex items-center justify-center">
        <LoadingSpinner size="medium" iconClassName="text-gray-400" />
      </div>
    )
  }

  if (error) {
    console.error('Image URL error:', error)
    const isAuthError = error.message?.includes('Authentication required')
    
    return (
      <div className="w-full h-full bg-gray-200 rounded-md flex items-center justify-center p-2" onClick={onClick}>
        <div className="text-center">
          {isAuthError ? (
            <>
              <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <span className="text-xs text-amber-700">Login required</span>
            </>
          ) : (
            <>
              <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <span className="text-xs text-gray-500">Failed to load</span>
            </>
          )}
        </div>
      </div>
    )
  }

  if (!imageUrl) {
    return (
      <div className="w-full h-full bg-gray-200 rounded-md flex items-center justify-center" onClick={onClick}>
        <div className="text-center">
          <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <span className="text-xs text-gray-500">No URL available</span>
        </div>
      </div>
    )
  }

  return (
    <ImageWithFallback
      src={imageUrl}
      alt={image.name}
      className="w-full h-full object-cover rounded-md cursor-pointer"
      onClick={onClick}
      fallbackClassName="w-full h-full bg-gray-200 rounded-md flex items-center justify-center"
      onError={() => console.error('ImageWithFallback onError called for:', image.name)}
    />
  )
}
